import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BUSINESS_TYPES,
  BusinessInformationDto,
  CustomerFormDtoTotal,
  CustomerFormState,
  SERVICES_OFFERED_LIST,
  ServicesOffered,
  STATES_LIST,
  WORKING_EXCEPTIONS_MOCK,
  WORKING_HOURS_MOCK,
} from 'src/app/models/customer-form.model';
import { UserProfile } from 'src/app/models/user-profile.model';
import { CustomerFormService } from 'src/app/services/customer-form/customer-form.service';
import { UserProfileService } from 'src/app/services/user-profile/user-profile.service';

@Component({
  selector: 'app-addrestaurant',
  templateUrl: './addrestaurant.component.html',
  styleUrls: ['./addrestaurant.component.css'],
})
export class AddrestaurantComponent implements OnInit {
  customerForm: FormGroup;
  isSubmitting = false;
  isLoadingProfile = false;
  userProfile: UserProfile | null = null;
  profileError: string | null = null;
  invalidLinkMessage = '';
  salePhone = '';
  hasSalePhone = false;

  // Data for dropdowns
  statesList = STATES_LIST;
  servicesOfferedList = SERVICES_OFFERED_LIST;
  businessTypesList = BUSINESS_TYPES;
  selectedServices: ServicesOffered[] = [];
  workingHoursMock = WORKING_HOURS_MOCK;
  workingExceptionsMock = WORKING_EXCEPTIONS_MOCK;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private customerFormService: CustomerFormService,
    private userProfileService: UserProfileService
  ) {
    this.customerForm = this.fb.group({
      businessName: ['', Validators.required],
      businessAddress: ['', Validators.required],
      businessAddress2: [''],
      businessCity: ['', Validators.required],
      businessState: ['', Validators.required],
      businessZipCode: [
        '',
        [Validators.required, Validators.pattern(/^\d{5}$/)],
      ],
      businessType: ['', Validators.required],
      businessPhone: [
        '',
        [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)],
      ],
      businessEmail: ['', [Validators.required, Validators.email]],
      servicesOffered: [[], Validators.required],
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const paramPhone = params.get('salePhone')?.trim() ?? '';
      this.salePhone = paramPhone || '17133643889';
      this.hasSalePhone = this.salePhone.length > 0;

      if (!this.hasSalePhone) {
        this.invalidLinkMessage =
          'Sale representative not found. Please contact your sale rep for more information.';
        this.router.navigate(['/sign-in']);
        return;
      }

      this.loadUserProfile();
    });
  }

  loadUserProfile(): void {
    if (!this.hasSalePhone) {
      return;
    }

    this.isLoadingProfile = true;
    this.profileError = null;
    this.userProfileService.checkUserExists(`+${this.salePhone}`).subscribe({
      next: (response) => {
        this.userProfile = response;
        if (!response) {
          this.invalidLinkMessage =
            'Sale representative not found. Please contact your sale rep for more information.';
        }
        this.isLoadingProfile = false;
      },
      error: (error) => {
        console.error('Profile error:', error);
        this.profileError =
          error?.error?.message ||
          error?.message ||
          'Unable to load user profile. Please try again later.';
        this.isLoadingProfile = false;
      },
    });
  }

  formatPhoneNumber(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length >= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(
        6,
        10
      )}`;
    } else if (value.length >= 3) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    }
    this.customerForm.patchValue(
      { businessPhone: value },
      { emitEvent: false }
    );
  }

  toggleService(service: ServicesOffered, isChecked: boolean): void {
    const index = this.selectedServices.indexOf(service);
    if (isChecked && index === -1) {
      this.selectedServices.push(service);
    }

    if (!isChecked && index > -1) {
      this.selectedServices.splice(index, 1);
    }

    this.customerForm.patchValue({
      servicesOffered: [...this.selectedServices],
    });
  }

  isServiceSelected(service: ServicesOffered): boolean {
    return this.selectedServices.includes(service);
  }

  capitalizeWords(str: string): string {
    return this.customerFormService.capitalizeWords(str);
  }

  hasControlError(controlName: string): boolean {
    const control = this.customerForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  async onSubmit(): Promise<void> {
    if (this.customerForm.invalid) {
      Object.keys(this.customerForm.controls).forEach((key) => {
        this.customerForm.get(key)?.markAsTouched();
      });
      alert('Please fill in all required fields correctly.');
      return;
    }

    if (!this.userProfile) {
      alert('User profile not found. Please refresh the page.');
      return;
    }

    const zipCode = this.customerForm.get('businessZipCode')?.value;
    if (!zipCode) {
      alert('Zip Code is required');
      return;
    }

    this.isSubmitting = true;
    const formData: CustomerFormState =
      this.customerForm.getRawValue() as CustomerFormState;

    try {
      let coordinates = { latitude: 0, longitude: 0 };
      try {
        const coordsResponse = await this.customerFormService
          .transferZipCodeToLatLong(zipCode)
          .toPromise();
        if (coordsResponse) {
          coordinates = coordsResponse;
        }
      } catch (coordsError) {
        console.warn(
          'Failed to fetch coordinates, continuing with defaults.',
          coordsError
        );
      }

      const businessPayload: BusinessInformationDto = {
        storeGroupId: '',
        website: '',
        zipCode: formData.businessZipCode,
        address: formData.businessAddress,
        address2: formData.businessAddress2,
        city: formData.businessCity,
        state: formData.businessState,
        storeName: formData.businessName,
        staffIds: [],
        ownerId: '',
        phoneNumber: this.customerFormService.formatPhoneNumberForDatabase(
          formData.businessPhone
        ),
        workingHours: this.workingHoursMock,
        holidayHours: this.workingExceptionsMock.holidayHours,
        dayOffHours: this.workingExceptionsMock.dayOffHours,
        description: '',
        type: formData.servicesOffered,
        storeLogo: '',
        banner: '',
        galleryPhotos: [],
        maxStaffCount: 5,
        activeStaffCount: 0,
        isSandbox: formData.businessType === 'sandbox',
        adminIds: [],
        status: 'draft',
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        email: formData.businessEmail,
        smsPhoneNumber: '',
      };

      const customerFormPayload: CustomerFormDtoTotal = {
        ...formData,
        repId: this.userProfile.id,
        status: 'draft',
      };

      await this.customerFormService
        .createCustomerForm(customerFormPayload)
        .toPromise();
      await this.customerFormService
        .createBusinessInformation(businessPayload, false, `+${this.salePhone}`)
        .toPromise();

      alert('Business registration completed successfully!');
      this.customerForm.reset({
        businessName: '',
        businessAddress: '',
        businessAddress2: '',
        businessCity: '',
        businessState: '',
        businessZipCode: '',
        businessType: '',
        businessPhone: '',
        businessEmail: '',
        servicesOffered: [],
      });
      this.selectedServices = [];
    } catch (error: any) {
      console.error('Submission error:', error);
      alert(
        'An error occurred during submission: ' +
          (error?.error?.message || error.message)
      );
    } finally {
      this.isSubmitting = false;
    }
  }
}
